/**
 * Pure client-side aggregation over the raw rows from ./queries.ts: per-report summaries
 * (for the "at-a-glance" badges), per-valuer counts (for Home), and the longest-waiting
 * report across all valuers. Kept dependency-free so it's trivially testable and reusable
 * between the Home and valuer-reports screens.
 */
import type { CheckResultRow, ErrorFindingRow, ReportRow, RiskResultRow, ValuerRow } from './types';

export interface ReportSummary {
  reportId: string;
  failCount: number;
  flagCount: number;
  riskFlagCount: number;
  errorCount: number;
  /** A clean report reads as clean instantly: no fails, flags, risk flags, or errors. */
  clean: boolean;
}

export function summariseReport(
  reportId: string,
  checks: CheckResultRow[],
  risks: RiskResultRow[],
  errors: ErrorFindingRow[],
): ReportSummary {
  let failCount = 0;
  let flagCount = 0;
  for (const c of checks) {
    if (c.reportId !== reportId) continue;
    if (c.status === 'fail') failCount += 1;
    else if (c.status === 'flag') flagCount += 1;
  }
  let riskFlagCount = 0;
  for (const r of risks) {
    if (r.reportId !== reportId) continue;
    if (r.status === 'missing_comment' || r.status === 'comment_inadequate') riskFlagCount += 1;
  }
  let errorCount = 0;
  for (const e of errors) {
    if (e.reportId === reportId) errorCount += 1;
  }
  return {
    reportId,
    failCount,
    flagCount,
    riskFlagCount,
    errorCount,
    clean: failCount === 0 && flagCount === 0 && riskFlagCount === 0 && errorCount === 0,
  };
}

/** A report needs the reviewer's attention if it's not yet done and isn't clean. */
export function needsAttention(status: string, summary: ReportSummary): boolean {
  return status !== 'done' && status !== 'error' && !summary.clean;
}

export interface ValuerSummary {
  valuerId: string;
  displayName: string;
  total: number;
  awaiting: number;
  done: number;
}

export function summariseValuers(valuers: ValuerRow[], reports: ReportRow[]): ValuerSummary[] {
  return valuers.map((v) => {
    const own = reports.filter((r) => r.valuerId === v.id);
    const done = own.filter((r) => r.status === 'done').length;
    return { valuerId: v.id, displayName: v.displayName, total: own.length, awaiting: own.length - done, done };
  });
}

/** The oldest still-open report (by submission time) across every valuer. */
export function longestWaiting(reports: ReportRow[]): ReportRow | null {
  const waiting = reports.filter((r) => r.status !== 'done' && r.status !== 'error');
  if (waiting.length === 0) return null;
  return waiting.reduce((oldest, r) => (waitingSince(r) < waitingSince(oldest) ? r : oldest));
}

export function waitingSince(report: ReportRow): number {
  return new Date(report.submittedAt ?? report.firstSeenAt).getTime();
}

/** Calendar-day bucket for a report's optional "needed by" deadline, or null if unset/future. */
export type DeadlineBucket = 'overdue' | 'due_today' | null;

export function deadlineBucket(neededByAt: string | null, now: Date = new Date()): DeadlineBucket {
  if (!neededByAt) return null;
  const due = new Date(neededByAt);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  if (dueDay < today) return 'overdue';
  if (dueDay === today) return 'due_today';
  return null;
}

/**
 * The single most urgent report by deadline: the nearest upcoming needed_by_at, or - if
 * any deadline has already passed - the most overdue one (same comparison, since the
 * smallest timestamp among a mix of passed/upcoming deadlines is either the oldest-overdue
 * or, when none are overdue, the soonest-upcoming). Independent of `longestWaiting` -
 * they can be different reports.
 */
export function mostUrgentByDeadline(reports: ReportRow[]): ReportRow | null {
  const withDeadline = reports.filter((r) => r.status !== 'done' && r.status !== 'error' && r.neededByAt);
  if (withDeadline.length === 0) return null;
  return withDeadline.reduce((soonest, r) =>
    new Date(r.neededByAt!).getTime() < new Date(soonest.neededByAt!).getTime() ? r : soonest,
  );
}
